import { Component, effect, Signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { GenericComponent } from '../generic/generic.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SidenavService } from 'src/app/shared/services/sidenav.service';
import { Store } from '@ngxs/store';
import { ConcessioncontrolState, PrepareDataFromKeys } from '../../states/concessioncontrol.state';
import { combineLatest, distinctUntilChanged } from 'rxjs';
import { CC_CONSTANTS, StrokeLineCap } from '../../assets/constants';
import { LegendConfig } from 'src/app/shared/components/map/map.component';
import {
    DefaultPolygonStyle,
    EmptyPolygonStyle,
    HatchImage,
    MapColorsAlt,
} from 'src/app/shared/components/map/map.constants';
import {
    colorAec1BlueLight,
    colorAec2BlueDark,
    colorAec3Green,
    colorAec4Orange,
    colorAec5Brown,
    colorAec6Gray,
    colorAec7RedLight,
    colorAec8RedDark,
    colorAec9Cream,
    colorBlack,
} from 'src/app/shared/all.constants';
import { PathOptions } from 'leaflet';
import { convertNumber } from 'src/app/shared/all.helpers';
import {
    ApexAxisChartSeries,
    ApexDataLabels,
    ApexLegend,
    ApexPlotOptions,
    ApexTooltip,
    ApexXAxis,
    ApexYAxis,
} from 'ng-apexcharts';
import {
    EDF_CUSTOMERS_CLAIM_DISTRIB_COMPLAINTS_CHART,
    EDF_CUSTOMERS_CLAIM_EVOL_COMLPLAINTS_CHART,
    EDF_CUSTOMERS_CLAIM_KPIS,
    EDF_CUSTOMERS_CLAIM_RATE_COMPARISON_CHART,
    EDF_CUSTOMERS_CLAIM_RATE_MAP,
    EDF_CUSTOMERS_CLAIM_RATE_RESPONSE_CHART,
} from '../../assets/keys';

const KEYS = [
    EDF_CUSTOMERS_CLAIM_KPIS,
    EDF_CUSTOMERS_CLAIM_RATE_COMPARISON_CHART,
    EDF_CUSTOMERS_CLAIM_RATE_MAP,
    EDF_CUSTOMERS_CLAIM_EVOL_COMLPLAINTS_CHART,
    EDF_CUSTOMERS_CLAIM_DISTRIB_COMPLAINTS_CHART,
    EDF_CUSTOMERS_CLAIM_RATE_RESPONSE_CHART,
];

interface ClaimKpiData {
    organizationId?: string;
    year?: string;
    error?: boolean;

    claimCount?: number;
    claimCountEvolution?: number;

    treatedClaimRate?: number;
    treatedClaimRateEvolution?: number;
}

interface edfClaimRateComparisonChartData {
    organizationId?: string;
    year?: string;
    category?: string;
    min?: number;
    max?: number;
    mean?: number;
    clientValue?: number;
    error?: boolean;
}

interface ComplChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    qualitySupply?: number;
    contract?: number;
    reception?: number;
    adviceService?: number;
    recovery?: number;
    report?: number;
    biling?: number;
    relationDistributor?: number;
    total?: number;
    error?: boolean;
}

interface DistribChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    internetMail?: number;
    letters?: number;
    error?: boolean;
}

interface RateChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    rateComplaints?: number;
    error?: boolean;
}

@UntilDestroy()
@Component({
    selector: 'app-edf-claims',
    templateUrl: './edf-claims.component.html',
    styleUrls: ['./edf-claims.component.scss'],
})
export class EdfClaimsComponent extends GenericComponent {
    public texts = CC_CONSTANTS.edfClaims.texts;

    public kpis: Signal<ClaimKpiData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(EDF_CUSTOMERS_CLAIM_KPIS),
    ) as Signal<ClaimKpiData[]>;

    public complChartError: boolean = true;
    public complChartSeries: ApexAxisChartSeries = [];
    public complDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        style: this.setDataLabelsStyleColors(this.texts.evolcomplaintsEdfChart.totalTitle),
    };
    public complXaxisCategories: ApexXAxis['categories'];
    public complYaxis: ApexYAxis[] = [
        {
            seriesName: this.texts.evolcomplaintsEdfChart.qualitySupplyAxisTitle,
            min: 0,
            title: {
                text: this.texts.evolcomplaintsEdfChart.yaxisTitleLeft,
            },
        },
    ];
    public complChartData: Signal<ComplChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(EDF_CUSTOMERS_CLAIM_EVOL_COMLPLAINTS_CHART),
    ) as Signal<ComplChartData[]>;
    public complLegend: ApexLegend = {
        markers: {
            fillColors: [
                colorAec2BlueDark,
                colorAec5Brown,
                colorAec3Green,
                colorAec9Cream,
                colorAec1BlueLight,
                colorAec4Orange,
                colorAec7RedLight,
                colorAec6Gray,
                colorBlack,
            ],
        },
    };
    public evolComplaintsCustomTooltip: ApexTooltip['custom'];

    public distribChartError: boolean = true;
    public distribChartSeries: ApexAxisChartSeries = [];
    public distribDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatterBySerie({ 0: null, 1: null }),
    };
    public distribXaxisCategories: ApexXAxis['categories'];
    public distribYaxis: ApexYAxis[] = [
        {
            seriesName: this.texts.distribComplaintsEdfChart.internetMailAxisTitle,
            min: 0,
            title: {
                text: this.texts.distribComplaintsEdfChart.yaxisTitleLeft,
            },
        },
        {
            seriesName: this.texts.distribComplaintsEdfChart.internetMailAxisTitle,
            min: 0,
        },
    ];
    public distribChartData: Signal<DistribChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(EDF_CUSTOMERS_CLAIM_DISTRIB_COMPLAINTS_CHART),
    ) as Signal<DistribChartData[]>;

    public rateChartError: boolean = true;
    public rateChartSeries: ApexAxisChartSeries = [];
    public rateDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatterBySerie({ 0: null }),
    };
    public rateXaxisCategories: ApexXAxis['categories'];
    public rateYaxis: ApexYAxis[] = [
        {
            seriesName: this.texts.evolRateComplaintsEdfChart.rateComplaintsAxisTitle,
            min: 50,
            title: {
                text: this.texts.evolRateComplaintsEdfChart.yaxisTitleLeft,
            },
        },
    ];
    public rateChartData: Signal<RateChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(EDF_CUSTOMERS_CLAIM_RATE_RESPONSE_CHART),
    ) as Signal<RateChartData[]>;
    public ratePlotOptions: ApexPlotOptions = {
        bar: {
            horizontal: false,
            dataLabels: {
                position: 'top',
            },
        },
    };

    public edfClaimRateComparisonChartSeries: ApexAxisChartSeries = [];
    public edfClaimRateComparisonChartDataSignal: Signal<edfClaimRateComparisonChartData[]> =
        this.store.selectSignal(
            ConcessioncontrolState.getDataByKey(EDF_CUSTOMERS_CLAIM_RATE_COMPARISON_CHART),
        ) as Signal<edfClaimRateComparisonChartData[]>;
    public rateComparisonDataLabels: ApexDataLabels = {
        enabled: false,
    };
    public rateComparisonPlotOptions: ApexPlotOptions = {
        bar: {
            horizontal: true,
            barHeight: this.setRangeBarHeight(1),
            borderRadius: 1,
        },
    };
    public rateComparisonCustomTooltip: ApexTooltip['custom'] = function ({
        series,
        seriesIndex,
        dataPointIndex,
        w,
    }) {
        const precision = CC_CONSTANTS.edfClaims.texts.edfClaimRateComparisonChart.precision;
        const dataSerie = w.globals.initialSeries[seriesIndex];
        const serieName = dataSerie.name;
        const serieColor = dataSerie.color;
        const dataPoint = dataSerie.data[dataPointIndex];
        const min = convertNumber(dataPoint.y[0], precision, false);
        const max = convertNumber(dataPoint.y[1], precision, false);

        const meanGoal = dataPoint.goals[0];
        const meanValue = convertNumber(meanGoal.value, precision, false);
        const meanName = meanGoal.name;
        const meanColor = meanGoal.strokeColor;

        const clientValueGoal = dataPoint.goals[1];
        const clientValue = convertNumber(clientValueGoal.value, precision, false);
        const clientValueName = clientValueGoal.name;
        const clientColor = clientValueGoal.strokeColor;

        return `
            <div class="apexcharts-tooltip-title">
                ${dataPoint.x}
            </div>

            <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1;display: flex;">
                <span class="apexcharts-tooltip-marker" style="background-color: ${serieColor}"></span>
                <div class="apexcharts-tooltip-text">
                    <div class="apexcharts-tooltip-y-group">
                        <span class="apexcharts-tooltip-text-y-label">${serieName}:</span>
                        <span class="apexcharts-tooltip-text-y-value"> ${min} - ${max} récla./10 000 clients TRV</span>
                    </div>
                        <div class="apexcharts-tooltip-goals-group">
                        </div>
                </div>
            </div>
            <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 2;display: flex;">
                <span class="apexcharts-tooltip-marker" style="background-color: ${meanColor}"></span>
                <div class="apexcharts-tooltip-text">
                    <div class="apexcharts-tooltip-y-group">
                        <span class="apexcharts-tooltip-text-y-label">${meanName}:</span>
                        <span class="apexcharts-tooltip-text-y-value"> ${meanValue} récla./10 000 clients TRV</span>
                    </div>
                        <div class="apexcharts-tooltip-goals-group">
                        </div>
                </div>
            </div>
                <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 3;display: flex;">
                <span class="apexcharts-tooltip-marker" style="background-color: ${clientColor}"></span>
                <div class="apexcharts-tooltip-text">
                    <div class="apexcharts-tooltip-y-group">
                        <span class="apexcharts-tooltip-text-y-label">${clientValueName}:</span>
                        <span class="apexcharts-tooltip-text-y-value"> ${clientValue} récla./10 000 clients TRV</span>
                    </div>

                </div>
            </div>
        `;
    };
    public edfClaimRateComparisonChartError: boolean = true;

    public geometries: Signal<any[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(EDF_CUSTOMERS_CLAIM_RATE_MAP),
    ) as Signal<any[]>;

    public legendConfig: LegendConfig[] = [
        {
            title: 'Taux de de réclamations traitées par EDF pour 10 000 clients aux TRV',
            values: [
                {
                    color: MapColorsAlt.darkGreen,
                    value: 'Moins de 150 réclamations pour 10 000 us',
                },
                {
                    color: MapColorsAlt.lightGreen,
                    value: 'Entre 150 et 250 réclamations pour 10 000 us',
                },
                {
                    color: MapColorsAlt.lighterGreen,
                    value: 'Entre 250 et 350 réclamations pour 10 000 us',
                },
                {
                    color: MapColorsAlt.paleGreen,
                    value: 'Entre 350 et 500 réclamations pour 10 000 us',
                },
                { color: colorAec4Orange, value: 'Entre 500 et 650 réclamations pour 10 000 us' },
                { color: colorAec5Brown, value: 'Entre 650 et 800 réclamations pour 10 000 us' },
                { color: colorAec8RedDark, value: 'Plus de 800 réclamations pour 10 000 us' },
                { color: `url(${HatchImage})`, value: 'Communes hors concession' },
            ],
        },
    ];

    constructor(
        private readonly route: ActivatedRoute,
        private readonly sidenavService: SidenavService,
        private readonly store: Store,
    ) {
        super(route, sidenavService, store);

        effect(() => {
            const edfClaimRateComparisonChartData = this.edfClaimRateComparisonChartDataSignal();
            this.edfClaimRateComparisonChartError = this.checkForError(
                edfClaimRateComparisonChartData,
                ['category', 'min', 'max', 'mean'],
            );
            if (!this.edfClaimRateComparisonChartError) {
                this.edfClaimRateComparisonChartSeries = [
                    {
                        name: this.texts.edfClaimRateComparisonChart.serieLabel,
                        data: edfClaimRateComparisonChartData.map((data) => ({
                            x: data.category,
                            y: [data.min, data.max],
                            goals: [
                                {
                                    name: this.texts.edfClaimRateComparisonChart.aecMeanLabel,
                                    value: data.mean!,
                                    strokeWidth: 10,
                                    strokeHeight: 0,
                                    strokeLineCap: StrokeLineCap.ROUND,
                                    strokeColor: colorAec8RedDark,
                                },

                                {
                                    name: this.texts.edfClaimRateComparisonChart.clientValueLabel,
                                    value: data.clientValue!,
                                    strokeWidth: 10,
                                    strokeHeight: 0,
                                    strokeLineCap: StrokeLineCap.ROUND,
                                    strokeColor: colorAec1BlueLight,
                                },
                            ],
                        })),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.edfClaimRateComparisonChart.serieLabelAvg,
                        type: 'bar',
                        data: [],
                        color: colorAec8RedDark,
                    },
                    {
                        name: this.texts.edfClaimRateComparisonChart.serieLabelValue,
                        type: 'bar',
                        data: [],
                        color: colorAec1BlueLight,
                    },
                ];
            }

            const complDataList = this.complChartData();
            this.complChartError = this.checkForError(complDataList, [
                'xYear',
                'qualitySupply',
                'contract',
                'reception',
                'adviceService',
                'recovery',
                'report',
                'biling',
                'relationDistributor',
            ]);

            if (!this.complChartError) {
                this.complXaxisCategories = complDataList.map((data) => data.xYear);
                const totales = this.getTotales(complDataList, [
                    'qualitySupply',
                    'contract',
                    'reception',
                    'adviceService',
                    'recovery',
                    'report',
                    'biling',
                    'relationDistributor',
                ]);
                this.complChartSeries = [
                    {
                        name: this.texts.evolcomplaintsEdfChart.qualitySupplyAxisTitle,
                        type: 'column',
                        data: complDataList.map((data) => data.qualitySupply!),
                        color: colorAec2BlueDark,
                    },
                    {
                        name: this.texts.evolcomplaintsEdfChart.contractAxisTitle,
                        type: 'column',
                        data: complDataList.map((data) => data.contract!),
                        color: colorAec5Brown,
                    },
                    {
                        name: this.texts.evolcomplaintsEdfChart.receptionAxisTitle,
                        type: 'column',
                        data: complDataList.map((data) => data.reception!),
                        color: colorAec3Green,
                    },
                    {
                        name: this.texts.evolcomplaintsEdfChart.adviceServiceAxisTitle,
                        type: 'column',
                        data: complDataList.map((data) => data.adviceService!),
                        color: colorAec9Cream,
                    },
                    {
                        name: this.texts.evolcomplaintsEdfChart.recoveryAxisTitle,
                        type: 'column',
                        data: complDataList.map((data) => data.recovery!),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.evolcomplaintsEdfChart.reportAxisTitle,
                        type: 'column',
                        data: complDataList.map((data) => data.report!),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.evolcomplaintsEdfChart.bilingAxisTitle,
                        type: 'column',
                        data: complDataList.map((data) => data.biling!),
                        color: colorAec7RedLight,
                    },
                    {
                        name: this.texts.evolcomplaintsEdfChart.relationDistributorAxisTitle,
                        type: 'column',
                        data: complDataList.map((data) => data.relationDistributor!),
                        color: colorAec6Gray,
                    },
                    {
                        name: this.texts.evolcomplaintsEdfChart.totalTitle,
                        type: 'line',
                        data: totales,
                        color: 'transparent',
                    },
                ];
                this.complDataLabels.enabledOnSeries = [0, 1, 2, 3, 4, 5, 6, 7, 8];
            }

            const distribDataList = this.distribChartData();
            this.distribChartError = this.checkForError(distribDataList, [
                'xYear',
                'internetMail',
                'letters',
            ]);

            if (!this.distribChartError) {
                this.distribXaxisCategories = distribDataList.map((data) => data.xYear);
                this.distribChartSeries = [
                    {
                        name: this.texts.distribComplaintsEdfChart.internetMailAxisTitle,
                        type: 'column',
                        data: distribDataList.map((data) => data.internetMail!),
                        color: colorAec3Green,
                    },
                    {
                        name: this.texts.distribComplaintsEdfChart.lettersAxisTitle,
                        type: 'column',
                        data: distribDataList.map((data) => data.letters!),
                        color: colorAec1BlueLight,
                    },
                ];

                this.distribDataLabels.enabledOnSeries = [0, 1];
            }

            const rateDataList = this.rateChartData();
            this.rateChartError = this.checkForError(rateDataList, ['xYear', 'rateComplaints']);

            if (!this.rateChartError) {
                this.rateXaxisCategories = rateDataList.map((data) => data.xYear);
                this.rateChartSeries = [
                    {
                        name: this.texts.evolRateComplaintsEdfChart.rateComplaintsAxisTitle,
                        type: 'column',
                        data: rateDataList.map((data) => data.rateComplaints!),
                        color: colorAec2BlueDark,
                    },
                ];

                this.rateDataLabels.enabledOnSeries = [0];
            }
        });
    }

    ngOnInit() {
        combineLatest([this._currentOrganization$, this._currentYear$])
            .pipe(untilDestroyed(this), distinctUntilChanged())
            .subscribe(() => {
                this.store.dispatch(new PrepareDataFromKeys({ keys: KEYS }));
            });

        this.evolComplaintsCustomTooltip = this.setEvolComplaintsCustomTooltip();
    }

    private setEvolComplaintsCustomTooltip(): ApexTooltip['custom'] {
        return ({ data, seriesIndex, dataPointIndex, w }) => {
            const collapsedSeriesIndices = w.globals.collapsedSeriesIndices;

            const categoryLabels = w.globals.categoryLabels;
            const title = categoryLabels[dataPointIndex];

            const precision = this.texts.evolcomplaintsEdfChart.precision;
            const qualitySupplySerie = w.globals.initialSeries[0];
            const {
                name: qualitySupplyName,
                color: qualitySupplyColor,
                valueStringified: qualitySupplyStringified,
            } = this.setCustomTooltipData(precision, qualitySupplySerie, dataPointIndex);

            const contractSerie = w.globals.initialSeries[1];
            const {
                name: contractName,
                color: contractColor,
                valueStringified: contractStringified,
            } = this.setCustomTooltipData(precision, contractSerie, dataPointIndex);

            const receptionSerie = w.globals.initialSeries[2];
            const {
                name: receptionName,
                color: receptionColor,
                valueStringified: receptionStringified,
            } = this.setCustomTooltipData(precision, receptionSerie, dataPointIndex);

            const adviceServiceSerie = w.globals.initialSeries[3];
            const {
                name: adviceServiceName,
                color: adviceServiceColor,
                valueStringified: adviceServiceStringified,
            } = this.setCustomTooltipData(precision, adviceServiceSerie, dataPointIndex);

            const recoverySerie = w.globals.initialSeries[4];
            const {
                name: recoveryName,
                color: recoveryColor,
                valueStringified: recoveryStringified,
            } = this.setCustomTooltipData(precision, recoverySerie, dataPointIndex);

            const reportSerie = w.globals.initialSeries[5];
            const {
                name: reportName,
                color: reportColor,
                valueStringified: reportStringified,
            } = this.setCustomTooltipData(precision, reportSerie, dataPointIndex);

            const bilingSerie = w.globals.initialSeries[6];
            const {
                name: bilingName,
                color: bilingColor,
                valueStringified: bilingStringified,
            } = this.setCustomTooltipData(precision, bilingSerie, dataPointIndex);

            const relationDistributorSerie = w.globals.initialSeries[7];
            const {
                name: relationDistributorName,
                color: relationDistributorColor,
                valueStringified: relationDistributorStringified,
            } = this.setCustomTooltipData(precision, relationDistributorSerie, dataPointIndex);

            const totalesSerie = w.globals.initialSeries[8];
            const {
                name: totalesName,
                color: totalesColor,
                valueStringified: totalesStringified,
            } = this.setCustomTooltipData(precision, totalesSerie, dataPointIndex);

            const dataTooltips = [
                {
                    color: qualitySupplyColor,
                    name: qualitySupplyName,
                    value: qualitySupplyStringified,
                },
                {
                    color: contractColor,
                    name: contractName,
                    value: contractStringified,
                },
                {
                    color: receptionColor,
                    name: receptionName,
                    value: receptionStringified,
                },
                {
                    color: adviceServiceColor,
                    name: adviceServiceName,
                    value: adviceServiceStringified,
                },
                {
                    color: recoveryColor,
                    name: recoveryName,
                    value: recoveryStringified,
                },
                {
                    color: reportColor,
                    name: reportName,
                    value: reportStringified,
                },
                {
                    color: bilingColor,
                    name: bilingName,
                    value: bilingStringified,
                },
                {
                    color: relationDistributorColor,
                    name: relationDistributorName,
                    value: relationDistributorStringified,
                },
                {
                    color: colorBlack,
                    name: totalesName,
                    value: totalesStringified,
                },
            ];
            const html = this.setCustomTooltip(collapsedSeriesIndices, title, dataTooltips);
            return html;
        };
    }

    public getMapStyle(feature: GeoJSON.Feature | undefined): PathOptions {
        if (!feature?.properties || feature.properties['edfClaimRate'] === null) {
            return EmptyPolygonStyle;
        }

        // Cities style
        let fillColor = MapColorsAlt.darkGreen;
        const edfClaimRate = Number(feature.properties['edfClaimRate']);
        if (edfClaimRate > 800) {
            fillColor = colorAec8RedDark;
        } else if (edfClaimRate > 650) {
            fillColor = colorAec5Brown;
        } else if (edfClaimRate > 500) {
            fillColor = colorAec4Orange;
        } else if (edfClaimRate > 350) {
            fillColor = MapColorsAlt.paleGreen;
        } else if (edfClaimRate > 250) {
            fillColor = MapColorsAlt.lighterGreen;
        } else if (edfClaimRate > 150) {
            fillColor = MapColorsAlt.lightGreen;
        }

        return {
            ...DefaultPolygonStyle,
            fillColor,
        };
    }

    public getTooltip(feature: GeoJSON.Feature): string {
        if (!feature.properties) {
            return '';
        }
        const { cityName, edfClaimRate, isInConcession } = feature.properties;
        if (!isInConcession) {
            return `${cityName} - Hors concession`;
        }

        const valueStringified = convertNumber(Number(edfClaimRate), null);
        return `${cityName} - ${valueStringified} réclamations d'EDF pour 10 000 usagers`;
    }
}
